import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message, Popconfirm, Tooltip, Descriptions, Alert, Typography, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SettingOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { unwrapGoNull } from '../../../shared/utils/goNull';

const { TextArea } = Input;
const { Text } = Typography;

// Value type options
const valueTypeOptions = [
    { label: '字符串', value: 'string' },
    { label: '整数', value: 'int' },
    { label: '布尔值', value: 'bool' },
    { label: 'JSON', value: 'json' },
];

// Predefined settings for quick add
// Only keys the mail server actually reads. (max_attachment_size / smtp_relay_enabled /
// default_quota_mb / spam_threshold were offered here but nothing consumed them; the
// attachment limit now comes from each plan's attachment_size_mb.)
const predefinedSettings = [
    { key: 'share_link_base_url', value_type: 'string', description: '附件分享链接的域名前缀，如 https://mail.example.com' },
];

const Settings = () => {
    const [settings, setSettings] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    // Fetch clusters
    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                // Select default cluster or first cluster
                const defaultCluster = list.find(c => c.is_default);
                if (defaultCluster) {
                    setSelectedCluster(defaultCluster.id);
                } else if (list.length > 0) {
                    setSelectedCluster(list[0].id);
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    // Fetch settings
    const fetchSettings = async () => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/settings', {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                const data = unwrapGoNull(res.data.data || {});
                // Node returns {success, settings: [{key, value, ...}]}; older shape was a key->item map.
                const entries = Array.isArray(data.settings)
                    ? data.settings.map((item) => [item.key, item])
                    : Object.entries(data).filter(([, item]) => item && typeof item === 'object');
                const settingsArray = entries.map(([key, item]) => ({
                    key,
                    value: item.value,
                    is_secret: !!item.is_secret,
                    value_type: item.value_type || 'string',
                    description: item.description || '',
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                }));
                setSettings(settingsArray);
            } else {
                message.error(res.data.msg || 'Failed to fetch settings');
            }
        } catch (error) {
            message.error('Failed to fetch settings: ' + (error.response?.data?.error || error.message));
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchSettings();
        }
    }, [selectedCluster]);

    const handleAdd = () => {
        setEditingKey(null);
        form.resetFields();
        form.setFieldsValue({
            value_type: 'string',
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingKey(record.key);
        form.setFieldsValue({
            key: record.key,
            value: record.value_type === 'json' ? JSON.stringify(record.value, null, 2) : String(record.value),
            value_type: record.value_type,
            description: record.description,
        });
        setModalVisible(true);
    };

    const handleDelete = async (key) => {
        try {
            const res = await adminApi.delete(`/webmail/settings/${encodeURIComponent(key)}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                message.success('Setting deleted');
                fetchSettings();
            } else {
                message.error(res.data.msg || 'Failed to delete');
            }
        } catch (error) {
            message.error('Failed to delete: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            // Convert value based on type
            let processedValue = values.value;
            if (values.value_type === 'int') {
                processedValue = parseInt(values.value, 10);
                if (isNaN(processedValue)) {
                    message.error('Invalid integer value');
                    return;
                }
            } else if (values.value_type === 'bool') {
                processedValue = values.value === 'true' || values.value === '1';
            } else if (values.value_type === 'json') {
                try {
                    processedValue = JSON.parse(values.value);
                } catch (e) {
                    message.error('Invalid JSON format');
                    return;
                }
            }

            const keyToUse = editingKey || values.key;
            const res = await adminApi.put(`/webmail/settings/${encodeURIComponent(keyToUse)}`, {
                // The node stores values as strings (value_type carries the type).
                value: typeof processedValue === 'object' ? JSON.stringify(processedValue) : String(processedValue),
                value_type: values.value_type,
                description: values.description,
            }, {
                params: { cluster_id: selectedCluster }
            });

            if (res.data.code === 0) {
                message.success(editingKey ? 'Setting updated' : 'Setting created');
                setModalVisible(false);
                fetchSettings();
            } else {
                message.error(res.data.msg || 'Failed to save');
            }
        } catch (error) {
            message.error('Failed to save: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleQuickAdd = (preset) => {
        setEditingKey(null);
        form.setFieldsValue({
            key: preset.key,
            value: '',
            value_type: preset.value_type,
            description: preset.description,
        });
        setModalVisible(true);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success('Copied to clipboard');
        });
    };

    const columns = [
        {
            title: '键名',
            dataIndex: 'key',
            width: 220,
            render: (text) => (
                <Space>
                    <Text code copyable={{ text }}>{text}</Text>
                </Space>
            ),
        },
        {
            title: '值',
            dataIndex: 'value',
            ellipsis: true,
            render: (value, record) => {
                if (record.value_type === 'json') {
                    const str = JSON.stringify(value);
                    return (
                        <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(value, null, 2)}</pre>}>
                            <Text ellipsis style={{ maxWidth: 200 }}>{str}</Text>
                        </Tooltip>
                    );
                }
                if (record.value_type === 'bool') {
                    return value ? <Tag color="green">true</Tag> : <Tag color="red">false</Tag>;
                }
                return <Text>{String(value)}</Text>;
            },
        },
        {
            title: '类型',
            dataIndex: 'value_type',
            width: 90,
            render: (type) => {
                const colors = { string: 'blue', int: 'green', bool: 'purple', json: 'orange' };
                return <Tag color={colors[type] || 'default'}>{type}</Tag>;
            },
        },
        {
            title: '说明',
            dataIndex: 'description',
            width: 240,
            ellipsis: true,
            render: (desc) => desc || '-',
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            width: 150,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip title="编辑">
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    </Tooltip>
                    <Popconfirm title="确定删除这项配置？" onConfirm={() => handleDelete(record.key)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <SettingOutlined />
                    邮件集群配置
                </Space>
            }
            extra={
                <Space>
                    <Select
                        style={{ width: 180 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={fetchSettings}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加配置
                    </Button>
                </Space>
            }
        >
            {!selectedCluster && (
                <Alert message="请先选择一个邮件集群" type="warning" showIcon style={{ marginBottom: 16 }} />
            )}

            <Alert
                message="这里的配置会同步到所选集群的所有邮件节点"
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
            />

            <Card size="small" title="常用配置项（点击添加）" style={{ marginBottom: 16 }}>
                <Space wrap>
                    {predefinedSettings.map(preset => (
                        <Tooltip key={preset.key} title={preset.description}>
                            <Button
                                size="small"
                                onClick={() => handleQuickAdd(preset)}
                                disabled={settings.some(s => s.key === preset.key) || !selectedCluster}
                            >
                                {preset.key}
                            </Button>
                        </Tooltip>
                    ))}
                </Space>
            </Card>

            <Table
                columns={columns}
                dataSource={settings}
                rowKey="key"
                loading={loading}
                size="small"
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedCluster ? '暂无配置，点击右上角“添加配置”或使用上方预设' : '请先选择集群'} /> }}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 项`,
                }}
            />

            <Modal
                title={editingKey ? `编辑配置：${editingKey}` : '添加配置'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editingKey && (
                        <Form.Item
                            name="key"
                            label="键名"
                            rules={[
                                { required: true, message: '请输入键名' },
                                { pattern: /^[a-z_][a-z0-9_]*$/, message: '只能用小写字母、数字和下划线' }
                            ]}
                        >
                            <Input placeholder="e.g., share_link_base_url" />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="value_type"
                        label="值类型"
                        rules={[{ required: true }]}
                    >
                        <Select options={valueTypeOptions} disabled={!!editingKey} />
                    </Form.Item>
                    <Form.Item
                        name="value"
                        label="值"
                        rules={[{ required: true, message: '请输入值' }]}
                        extra={
                            form.getFieldValue('value_type') === 'bool'
                                ? '填 true 或 false'
                                : form.getFieldValue('value_type') === 'json'
                                    ? '填合法 JSON'
                                    : null
                        }
                    >
                        {form.getFieldValue('value_type') === 'json' ? (
                            <TextArea rows={4} placeholder='{"key": "value"}' style={{ fontFamily: 'monospace' }} />
                        ) : form.getFieldValue('value_type') === 'bool' ? (
                            <Select>
                                <Select.Option value="true">true</Select.Option>
                                <Select.Option value="false">false</Select.Option>
                            </Select>
                        ) : (
                            <Input placeholder="配置值" />
                        )}
                    </Form.Item>
                    <Form.Item name="description" label="说明">
                        <Input placeholder="可选" />
                    </Form.Item>
                    <Form.Item style={{ marginTop: 16 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Settings;
